/**
 * Embed Video Viewer Route
 *
 * Minimal video viewer for iframe embedding.
 * No header, footer, or branding - just the player.
 * No authentication required.
 */

import { createFileRoute, notFound } from '@tanstack/react-router';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Loader2, Minimize2, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import type { VideoManifest, VideoPlaybackState } from '../types/videoManifest';
import { VideoCompositionPlayer, type VideoCompositionPlayerRef } from '../components/video-workspace/VideoCompositionPlayer';
import { VisibilityPauseOverlay } from '../components/video-workspace/VisibilityPauseOverlay';
import { GsapPlayerControls } from '../components/slides/GsapPlayerControls';
import { SegmentedProgressBar } from '../components/slides/SegmentedProgressBar';
import { DEFAULT_SLIDE_RESOLUTION } from '../config/gsapViewer';
import { SlidesModeControls, type PlayerMode } from '../components/player';
import { useAutoHideControls } from '../hooks/useAutoHideControls';
import { useSwipeGesture } from '../hooks/useSwipeGesture';
import { useTouchDevice } from '../hooks/useTouchDevice';
import { useContainerSize } from '../hooks/useContainerSize';
import { useViewportOrientation } from '../hooks/useViewportOrientation';
import { RotateDeviceHint } from '../components/player/RotateDeviceHint';
import { useScrollVisibility } from '../hooks/useScrollVisibility';
import { useVideoAnalytics } from '../hooks/useVideoAnalytics';
import { ImmersiveFullscreen } from '../lib/immersiveFullscreen';

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface VideoInfo {
  title: string | null;
  slideCount: number;
  totalDuration: number;
  thumbnailUrl: string | null;
}

interface PublicVideoResponse {
  success: boolean;
  videoInfo: VideoInfo;
  author: string | null;
  staticBundleUrl: string | null;
}

export const Route = createFileRoute('/embed/$shareToken')({
  component: EmbedVideoViewer,
  loader: async ({ params }) => {
    const { shareToken } = params;

    if (!shareToken) {
      throw notFound();
    }

    try {
      // Fetch public video info from API (validates the token)
      const response = await fetch(`${API_BASE_URL}/api/video-share/${shareToken}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw notFound();
        }
        throw new Error('Failed to load video');
      }

      const data: PublicVideoResponse = await response.json();

      if (!data.success) {
        throw notFound();
      }

      return {
        shareToken,
        videoInfo: data.videoInfo,
        staticBundleUrl: data.staticBundleUrl ?? null,
        manifestUrl: `${API_BASE_URL}/api/video-share/${shareToken}/manifest.json`,
      };
    } catch (error) {
      console.error('Error loading embed video:', error);
      throw notFound();
    }
  },
  notFoundComponent: () => (
    <div className="w-full h-full bg-black flex items-center justify-center">
      <p className="text-white/60 text-sm">Video not available</p>
    </div>
  ),
});

/**
 * Router: when the fast edge bundle exists (resolved server-side — the browser
 * can't reliably probe the CDN cross-origin), redirect the embed iframe straight
 * to it. The vplay player emits the same host embed contract (videoStateChange /
 * embedCommand) so partner integrations keep working, now edge-served. Falls back
 * to the legacy inline embed player when not yet baked.
 */
function EmbedVideoViewer() {
  const { staticBundleUrl } = Route.useLoaderData();
  useEffect(() => {
    if (staticBundleUrl) {
      const search = typeof window !== 'undefined' ? window.location.search : '';
      window.location.replace(staticBundleUrl + search);
    }
  }, [staticBundleUrl]);
  if (staticBundleUrl) return <div style={{ width: '100%', height: '100%', background: '#000' }} />;
  return <LegacyEmbedViewer />;
}

function LegacyEmbedViewer() {
  const { shareToken, videoInfo, manifestUrl } = Route.useLoaderData();

  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<VideoCompositionPlayerRef>(null);

  const { compact } = useContainerSize(containerRef);
  const isTouchDevice = useTouchDevice();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [manifest, setManifest] = useState<VideoManifest | null>(null);
  // Short-lived session token gating manifest + slide fetches (minted on load).
  const [sessionToken, setSessionToken] = useState<string | undefined>(undefined);

  // Derive resolution from manifest metadata (supports 16:9, 1:1, 9:16)
  const resolution = manifest?.metadata?.resolution ?? DEFAULT_SLIDE_RESOLUTION;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // GA4 video analytics
  const analytics = useVideoAnalytics({
    shareToken,
    manifest,
    isEmbed: true,
  });

  // Locked to 'video' — slides mode is unplugged (see video.$shareToken.tsx note). useState keeps
  // the union type so the dormant slides-mode branches type-check; no setter is exposed to flip it.
  const [playerMode] = useState<PlayerMode>('video');

  const [playbackState, setPlaybackState] = useState<VideoPlaybackState>({
    isPlaying: false,
    currentTime: 0,
    totalDuration: videoInfo.totalDuration || 0,
    currentSlideId: '',
    currentSlideIndex: 0,
    currentSlideTime: 0,
    currentSlideDuration: 0,
    playbackRate: 1,
    isContinuousMode: true,
    isTransitioning: false,
  });

  // Fetch manifest on mount
  useEffect(() => {
    const fetchManifest = async () => {
      try {
        setIsLoading(true);

        // Mint a short-lived, share-scoped session token (gates manifest + slides).
        let token: string | undefined;
        try {
          const sessionRes = await fetch(
            `${API_BASE_URL}/api/video-share/${shareToken}/session`,
            { method: 'POST' }
          );
          if (sessionRes.ok) token = (await sessionRes.json()).token;
        } catch (e) {
          console.warn('[VideoShare] session mint failed, continuing:', e);
        }
        setSessionToken(token);

        const response = await fetch(
          manifestUrl,
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
        );

        if (!response.ok) {
          throw new Error('Failed to load video manifest');
        }

        const data = await response.json();
        setManifest(data);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching manifest:', err);
        setError('Failed to load video');
        setIsLoading(false);
      }
    };

    fetchManifest();
  }, [manifestUrl, shareToken]);

  // Handle playback state changes from player
  const handleStateChange = useCallback((state: VideoPlaybackState) => {
    setPlaybackState(state);
    analytics.onStateChange(state);

    // Post message to parent for external control (include mode)
    window.parent.postMessage({
      type: 'videoStateChange',
      state,
      mode: playerMode,
    }, '*');
  }, [playerMode, analytics]);

  // Player control handlers
  // Tiered fullscreen delegated to the shared controller (iOS → drag-proof CSS
  // overlay; desktop/Android → native). onChange drives React state + analytics;
  // it also owns the immersive lock, embed handshake, and audio resume.
  const fsRef = useRef<ImmersiveFullscreen | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const fs = new ImmersiveFullscreen({
      container: el,
      isPlaying: () => playerRef.current?.getState().isPlaying ?? false,
      resume: () => playerRef.current?.play(),
      onChange: (active) => { setIsFullscreen(active); analytics.trackFullscreen(active); },
    });
    fsRef.current = fs;
    return () => { fs.destroy(); fsRef.current = null; };
  }, [analytics]);

  const enterFullscreen = useCallback(() => {
    if (!fsRef.current?.isActive()) fsRef.current?.toggle();
  }, []);

  // Auto-enter fullscreen on the first play (user gesture). Phone-only, and ONLY when the embed is
  // opened standalone (top-level) — never when embedded in a host page, so we don't hijack it.
  const autoFsAttempted = useRef(false);
  const handlePlayPause = useCallback(() => {
    const playing = playerRef.current?.getState().isPlaying ?? false;
    const standalone = typeof window !== 'undefined' && window.self === window.top;
    if (!playing && !autoFsAttempted.current && isTouchDevice && standalone) {
      autoFsAttempted.current = true;
      enterFullscreen();
    }
    playerRef.current?.togglePlay();
  }, [isTouchDevice, enterFullscreen]);

  const handlePlay = useCallback(() => {
    playerRef.current?.play();
  }, []);

  const handlePause = useCallback(() => {
    playerRef.current?.pause();
  }, []);

  const handleSeek = useCallback((time: number) => {
    // Preserve playing state through the seek (YouTube/Vimeo behavior).
    const wasPlaying = playerRef.current?.getState().isPlaying ?? false;
    playerRef.current?.seek(time, { pauseAfterSeek: !wasPlaying });
  }, []);

  // Slide-granular timeline: one segment per slide, width proportional to duration.
  const segments = useMemo(
    () =>
      (manifest?.slides ?? []).map((s) => ({
        id: s.id,
        title: s.title,
        duration: s.endTime != null && s.startTime != null
          ? s.endTime - s.startTime
          : (s.duration ?? 0),
      })),
    [manifest]
  );

  const slideStartTimes = useMemo(() => {
    const starts: number[] = [];
    let acc = 0;
    for (const s of manifest?.slides ?? []) {
      starts.push(s.startTime ?? acc);
      acc = s.endTime ?? acc + (s.duration ?? 0);
    }
    return starts;
  }, [manifest]);

  // Jump to a slide's START, preserving play state. The slide always begins from the beginning —
  // no mid-slide seek, no drift correction.
  const handleSlideJump = useCallback((index: number) => {
    if (!manifest || index < 0 || index >= manifest.slides.length) return;
    const wasPlaying = playerRef.current?.getState().isPlaying ?? false;
    playerRef.current?.seek(slideStartTimes[index] ?? 0, { pauseAfterSeek: !wasPlaying });
  }, [manifest, slideStartTimes]);

  const handleSpeedChange = useCallback((rate: number) => {
    playerRef.current?.setPlaybackRate(rate);
  }, []);

  // Slides mode navigation handlers
  const handlePreviousSlide = useCallback(() => {
    if (!manifest || playbackState.currentSlideIndex <= 0) return;
    const prevSlideId = manifest.slides[playbackState.currentSlideIndex - 1].id;
    playerRef.current?.navigateSlideWithTransition(prevSlideId, 'prev');
  }, [manifest, playbackState.currentSlideIndex]);

  const handleNextSlide = useCallback(() => {
    if (!manifest || playbackState.currentSlideIndex >= manifest.slides.length - 1) return;
    const nextSlideId = manifest.slides[playbackState.currentSlideIndex + 1].id;
    playerRef.current?.navigateSlideWithTransition(nextSlideId, 'next');
  }, [manifest, playbackState.currentSlideIndex]);

  // Resume from visibility pause
  const handleVisibilityResume = useCallback(() => {
    playerRef.current?.play();
  }, []);

  // Scroll visibility detection for embedded players
  const { pausedByScroll, handleScrollResume, clearScrollPause } = useScrollVisibility(
    containerRef,
    playbackState.isPlaying,
    handlePause
  );

  // Combined resume handler for scroll pause overlay
  const handleScrollResumeWithPlay = useCallback(() => {
    handleScrollResume();
    playerRef.current?.play();
  }, [handleScrollResume]);

  // Clear scroll pause state when user plays via controls
  useEffect(() => {
    if (playbackState.isPlaying) {
      clearScrollPause();
    }
  }, [playbackState.isPlaying, clearScrollPause]);

  // Fullscreen toggle — the controller decides tier and fires onChange.
  const handleFullscreen = useCallback(() => {
    fsRef.current?.toggle();
  }, []);

  const autoHide = useAutoHideControls({ enabled: true });

  // Two-tier controls: full pill shows when summoned (recent activity) or while paused; otherwise
  // it collapses to the slim resting bar so it never covers content mid-play.
  const showFullControls = !autoHide.hidden || !playbackState.isPlaying;

  // Rotate nudge (mobile): suggest rotating when device orientation ≠ video aspect. Never forced.
  const orientation = useViewportOrientation();
  const videoIsLandscape = resolution.width > resolution.height;
  const videoIsPortrait = resolution.height > resolution.width;
  const showRotateHint = isTouchDevice && !!manifest && !isLoading && !error &&
    ((videoIsLandscape && orientation === 'portrait') || (videoIsPortrait && orientation === 'landscape'));

  // On TOUCH devices, controls are summoned ONLY by tapping the bottom bar. Ignore activity-based
  // summon (incl. the synthetic mouse events a tap fires) so interacting with the slide never makes
  // the panel reappear. Desktop (mouse) still reveals controls on movement.
  const handleUserActivity = useCallback(() => {
    if (isTouchDevice) return;
    autoHide.show();
  }, [autoHide, isTouchDevice]);

  // Swipe is a slides-mode gesture; slides mode is unplugged and video mode must not navigate
  // by swipe. Disabled. (handlers kept for the dormant slides-mode path.)
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: handleNextSlide,
    onSwipeRight: handlePreviousSlide,
    enabled: false,
  });

  // Show controls briefly on slide navigation
  useEffect(() => {
    if (playerMode === 'slides') autoHide.show();
  }, [playbackState.currentSlideIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard shortcuts (desktop only). Scoped to the iframe document; ignored when text fields
  // are focused so the parent page's typing UX isn't hijacked.
  useEffect(() => {
    if (isTouchDevice) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key;
      if (key === ' ' || key === 'k' || key === 'K') {
        e.preventDefault();
        playerRef.current?.togglePlay();
        autoHide.show();
        return;
      }
      // Slide-granular navigation: arrows / j / l step between slides (each starts from its beginning).
      if (key === 'ArrowRight' || key === 'l' || key === 'L') {
        e.preventDefault();
        handleSlideJump(playbackState.currentSlideIndex + 1);
        autoHide.show();
        return;
      }
      if (key === 'ArrowLeft' || key === 'j' || key === 'J') {
        e.preventDefault();
        handleSlideJump(playbackState.currentSlideIndex - 1);
        autoHide.show();
        return;
      }
      if (key === 'f' || key === 'F') {
        e.preventDefault();
        handleFullscreen();
        return;
      }
      if (key === 'Home') {
        e.preventDefault();
        handleSlideJump(0);
        autoHide.show();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isTouchDevice, playbackState.currentSlideIndex, handleSlideJump, handleFullscreen, autoHide]);

  // Listen for commands from parent frame
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'embedCommand') {
        const { command, args } = event.data;

        switch (command) {
          case 'play':
            handlePlay();
            break;
          case 'pause':
            handlePause();
            break;
          case 'seek':
            if (typeof args?.time === 'number') {
              handleSeek(args.time);
            }
            break;
          case 'setSpeed':
            if (typeof args?.rate === 'number') {
              handleSpeedChange(args.rate);
            }
            break;
          // 'setMode' is a no-op while slides mode is unplugged (player is locked to video).
          case 'nextSlide':
            handleNextSlide();
            break;
          case 'prevSlide':
            handlePreviousSlide();
            break;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handlePlay, handlePause, handleSeek, handleSpeedChange, handleNextSlide, handlePreviousSlide]);

  // Scale calculation - fill the container using actual video resolution
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        const scaleX = containerWidth / resolution.width;
        const scaleY = containerHeight / resolution.height;
        const optimalScale = Math.min(scaleX, scaleY);

        setScale(optimalScale);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);

    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(updateScale);
      resizeObserver.observe(containerRef.current);

      return () => {
        window.removeEventListener('resize', updateScale);
        resizeObserver.disconnect();
      };
    }

    return () => window.removeEventListener('resize', updateScale);
  }, [resolution.width, resolution.height]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'w-full h-full bg-black flex items-center justify-center overflow-hidden relative',
        isFullscreen && 'fixed inset-0 z-[2147483647] w-screen h-[100dvh]',
        autoHide.hidden && 'cursor-none'
      )}
      {...swipeHandlers}
      onMouseMove={() => handleUserActivity()}
      onMouseLeave={() => autoHide.hideNow()}
    >
      {/* Poster thumbnail — shown immediately while JS/manifest loads */}
      {videoInfo.thumbnailUrl && (isLoading || !manifest) && (
        <img
          src={videoInfo.thumbnailUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          style={{ zIndex: 0 }}
        />
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center z-10">
          <Loader2 className="h-8 w-8 animate-spin text-white/60" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="text-center text-white/60">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Video Player */}
      {manifest && !isLoading && !error && (
        <>
          {/* Player area — video + overlaid controls */}
          <div
            className="relative"
            style={{
              width: `${resolution.width * scale}px`,
              height: `${resolution.height * scale}px`,
            }}
          >
            {/* Video with clipping */}
            <div className="absolute inset-0 bg-white overflow-hidden">
              <VideoCompositionPlayer
                ref={playerRef}
                manifest={manifest}
                conversationId={shareToken}
                authToken={sessionToken}
                onStateChange={handleStateChange}
                onManifestUpdate={setManifest}
                onSlideStart={analytics.onSlideStart}
                onVideoEnd={analytics.onVideoEnd}
                onError={analytics.onError}
                onUserActivity={handleUserActivity}
                className="w-full h-full"
              />

              {/* Visibility/scroll pause overlay */}
              {(playbackState.pausedByVisibility || pausedByScroll) && (
                <VisibilityPauseOverlay
                  onResume={pausedByScroll ? handleScrollResumeWithPlay : handleVisibilityResume}
                />
              )}
            </div>

            {/* Center play icon when paused */}
            {!playbackState.isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                <button
                  type="button"
                  onClick={handlePlayPause}
                  aria-label="Play"
                  className="pointer-events-auto w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
                >
                  <Play className="h-7 w-7 text-white ml-1 fill-current" />
                </button>
              </div>
            )}

            {/* Slides Mode Controls */}
            {playerMode === 'slides' && (
              <SlidesModeControls
                currentSlideIndex={playbackState.currentSlideIndex}
                totalSlides={manifest.slides.length}
                onPrevious={handlePreviousSlide}
                onNext={handleNextSlide}
                onFullscreen={handleFullscreen}
                isFullscreen={isFullscreen}
                currentSlideTitle={manifest.slides[playbackState.currentSlideIndex]?.title}
                hidden={autoHide.hidden}
                compact={compact}
              />
            )}
          </div>

          {/* Video controls — anchored to the PLAYER-AREA bottom (not the letterboxed video). */}
          {playerMode === 'video' && (
            <>
              {/* Gradient scrim — only while full controls are summoned */}
              <div className={cn(
                "absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/70 to-transparent pointer-events-none z-20 transition-opacity duration-300",
                showFullControls ? "opacity-100" : "opacity-0"
              )} />

              {/* Slim resting bar — TAP to summon full controls. Content taps pass to the slide. */}
              <div
                role="button"
                tabIndex={showFullControls ? -1 : 0}
                onClick={() => autoHide.show()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); autoHide.show(); } }}
                aria-label="Show player controls"
                className={cn(
                  "absolute left-0 right-0 bottom-0 z-20 px-3 pt-4 pb-1.5 cursor-pointer transition-opacity duration-300",
                  showFullControls ? "opacity-0 pointer-events-none" : "opacity-100"
                )}
              >
                <SegmentedProgressBar
                  slim
                  segments={segments}
                  currentSlideIndex={playbackState.currentSlideIndex}
                  currentSlideTime={playbackState.currentSlideTime}
                  currentSlideDuration={playbackState.currentSlideDuration}
                  onSelect={handleSlideJump}
                />
              </div>

              {/* Full controls — summoned, auto-collapse back to the slim bar */}
              <div className={cn(
                "absolute left-0 right-0 z-30 transition-all duration-300",
                compact ? "px-2 bottom-2" : "px-4 bottom-3",
                showFullControls ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
              )}>
                {compact ? (
                  <GsapPlayerControls
                    isPlaying={playbackState.isPlaying}
                    currentTime={playbackState.currentTime}
                    duration={playbackState.totalDuration}
                    playbackRate={playbackState.playbackRate}
                    isLoading={false}
                    onPlayPause={handlePlayPause}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onSeek={handleSeek}
                    segments={segments}
                    onSlideSelect={handleSlideJump}
                    currentSlideIndex={playbackState.currentSlideIndex}
                    currentSlideTime={playbackState.currentSlideTime}
                    currentSlideDuration={playbackState.currentSlideDuration}
                    onRestart={() => handleSlideJump(0)}
                    onStepBackward={() => handleSlideJump(playbackState.currentSlideIndex - 1)}
                    onStepForward={() => handleSlideJump(playbackState.currentSlideIndex + 1)}
                    onFullscreen={handleFullscreen}
                    compact
                  />
                ) : (
                  <div className="max-w-2xl mx-auto">
                    <GsapPlayerControls
                      isPlaying={playbackState.isPlaying}
                      currentTime={playbackState.currentTime}
                      duration={playbackState.totalDuration}
                      playbackRate={playbackState.playbackRate}
                      isLoading={false}
                      onPlayPause={handlePlayPause}
                      onPlay={handlePlay}
                      onPause={handlePause}
                      onSeek={handleSeek}
                      segments={segments}
                      onSlideSelect={handleSlideJump}
                      currentSlideIndex={playbackState.currentSlideIndex}
                      currentSlideTime={playbackState.currentSlideTime}
                      currentSlideDuration={playbackState.currentSlideDuration}
                      onRestart={() => handleSlideJump(0)}
                      onStepBackward={() => handleSlideJump(playbackState.currentSlideIndex - 1)}
                      onStepForward={() => handleSlideJump(playbackState.currentSlideIndex + 1)}
                      onFullscreen={handleFullscreen}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Rotate nudge — mobile, when device orientation ≠ video orientation */}
          {showRotateHint && <RotateDeviceHint target={videoIsLandscape ? 'landscape' : 'portrait'} />}

          {/* Floating mode toggle unplugged — player is locked to video. */}

          {/* Fullscreen Exit Button - top-left */}
          {isFullscreen && (
            <Button
              onClick={handleFullscreen}
              variant="ghost"
              size="sm"
              className="absolute top-3 left-3 z-30 text-white/70 hover:text-white hover:bg-white/10"
            >
              <Minimize2 className="h-5 w-5" />
            </Button>
          )}
        </>
      )}
    </div>
  );
}
